/**
 * Compact, persistent event-lifecycle uncertainty status (#481, #599).
 *
 * This component is passive by construction: it accepts no order or action
 * handler. Uncertainty ends only when `certainty` returns to `trusted`; there
 * is deliberately no dismiss control. The always-mounted polite live region
 * lets an already-open surface announce that transition without reserving any
 * layout space while the state is trusted.
 *
 * The visible treatment is a chit, not a warning panel. The complete reason
 * remains in the browser tooltip and accessible label, so repeated order cards
 * do not grow a dedicated explanatory row.
 */

import { AlertTriangle, HelpCircle } from 'lucide-react';
import type { EventStateCertainty } from '@/lib/sport/eventStateCertainty';

interface WarningCopy {
  label: string;
  detail: string;
}

const COPY: Record<Exclude<EventStateCertainty, 'trusted'>, WarningCopy> = {
  unknown: {
    label: 'State unconfirmed',
    detail:
      'No current authoritative source can confirm this event\'s lifecycle state. The order may be resting on a live or completed market. Edit and cancel remain available.',
  },
  stale: {
    label: 'State stale',
    detail:
      'The game-status feed is too old to trust, so this event may have started or ended without the Dash showing it. Edit and cancel remain available.',
  },
  contradictory: {
    label: 'State conflict',
    detail:
      'The game-status feed and this market disagree about the event lifecycle, possibly after a schedule change. Treat the state as unconfirmed; edit and cancel remain available.',
  },
};

export function EventStateWarning({
  certainty,
  className,
  display = 'label',
}: {
  certainty: EventStateCertainty;
  /** Host-surface placement only. Label mode applies it to the inner chit so
   * trusted state stays geometrically empty; icon mode applies it to the
   * intentionally reserved fixed-size slot. */
  className?: string;
  /** Dense order cards use a fixed-size icon so secondary lifecycle context
   * cannot squeeze or word-wrap the primary order identity. Other surfaces
   * keep the explicit label. */
  display?: 'label' | 'icon';
}) {
  const copy = certainty === 'trusted' ? null : COPY[certainty];
  const Icon = certainty === 'unknown' ? HelpCircle : AlertTriangle;

  if (display === 'icon') {
    return (
      <div role="status" className={`inline-flex h-5 w-5 shrink-0 ${className ?? ''}`}>
        {copy ? (
          <span
            tabIndex={0}
            title={copy.detail}
            aria-label={`${copy.label}. ${copy.detail}`}
            data-testid={`event-state-warning-${certainty}`}
            className={`inline-flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded border focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70 ${
              certainty === 'unknown'
                ? 'border-muted-foreground/40 bg-muted/40 text-muted-foreground'
                : 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300'
            }`}
          >
            <Icon aria-hidden="true" size={11} className="shrink-0" />
          </span>
        ) : (
          <span aria-hidden="true" className="h-5 w-5" />
        )}
      </div>
    );
  }

  return (
    <div role="status">
      {copy && (
        <span
          tabIndex={0}
          title={copy.detail}
          aria-label={`${copy.label}. ${copy.detail}`}
          data-testid={`event-state-warning-${certainty}`}
          className={`inline-flex min-h-[calc(1lh+6px)] shrink-0 cursor-help items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70 ${
            certainty === 'unknown'
              ? 'border-muted-foreground/40 bg-muted/40 text-muted-foreground'
              : 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          } ${className ?? ''}`}
        >
          <Icon aria-hidden="true" size={10} className="shrink-0" />
          {/* The label inherits its host's font. Centring its line box left
              the caps 2px high in the Tracker's mono rows (#976), so the label
              is trimmed to cap height; min-h above keeps the pill's old height
              (one line + padding + border). No text-box support: as before. */}
          <span className="[text-box:trim-both_cap_alphabetic]">{copy.label}</span>
        </span>
      )}
    </div>
  );
}
